import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { memo, useRef } from 'react';
import type { ObjectDefinitionSpec } from 'shared-types';
import { T } from '../../../translation/src';
import { fitFootprintToDrawing } from '../document/specOps';
import { useEditorState, useObjectEditor } from '../EditorContext';
import { FieldRow, NumberField, PanelButton, PanelSection, Segmented, ToggleRow } from '../panels/fields';
import { ModelSettings } from './ModelSettings';
import { ObjectDetails } from './ObjectDetails';

type Spec = ObjectDefinitionSpec;

/** Name/category/tags (saved immediately, not part of the draft) and the object's placement rules. */
const ObjectSettings_: React.FC = () => {
  const { store } = useObjectEditor();
  const { spec } = useEditorState();

  const setSpec = (update: (s: Spec) => Spec) => store.apply(update);
  const sizeEditBase = useRef<Spec | null>(null);
  const resizeFromBase = (axis: 'width' | 'depth' | 'height', value: number) => {
    const base = sizeEditBase.current ?? store.getState().spec;
    const size = { ...base.defaultSize, [axis]: value };
    // The drawing is authored at the default size and never scales with it here: the resize rules
    // (and repeats) only apply to test and placed instances.
    store.apply(() => ({ ...base, defaultSize: size }));
  };
  const isLine = spec.anchor === 'LINE';
  const handles = spec.handles;
  /** Doors / windows: fixed category, sized by their opening — no placement, depth, handles or 3D model. */
  const opening = spec.opening ?? null;
  /** Dimensions: fixed category, drawn between two points — only the default length is theirs to set. */
  const dimension = !!spec.dimension;
  const sizeAxes = opening ? (['width', 'height'] as const) : dimension ? (['width'] as const) : (['width', 'depth', 'height'] as const);

  return (
    <>
      <ObjectDetails />

      {opening && (
        <PanelSection title={opening.kind === 'DOOR' ? <T _str="door" /> : <T _str="window" />}>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {opening.kind === 'DOOR' ? (
              <T
                _str="offered in the door type dropdown of doors in projects. Draw it hinged at the left jamb and opening upward; placed doors mirror it for their hinge and side, scale it with their width and bend it along curved walls. Formulas can read the wall thickness as wall."
                swc
              />
            ) : (
              <T
                _str="offered in the window type dropdown of windows in projects. Draw it with the outside upward; placed windows mirror it for their side, scale it with their width and bend it along curved walls. Formulas can read the wall thickness as wall."
                swc
              />
            )}
          </p>
        </PanelSection>
      )}

      {dimension && (
        <PanelSection title={<T _str="dimension" />}>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            <T
              _str="offered in the dimension tool of the design tab. Draw the measured line from the left to the right point on the middle line. The line stretches with the measured length. Pin the end symbols to their end so they keep their size. Parameters can switch parts on and off. The length label is added for you."
              swc
            />
          </p>
        </PanelSection>
      )}

      {!opening && !dimension && (
        <PanelSection title={<T _str="placement" />}>
          <FieldRow label={<T _str="placed by" swc />} hint={isLine ? <T _str="drawn as a run between two points; its length follows the drawing" swc /> : <T _str="placed with one click, then moved, rotated and resized" swc />}>
            <Segmented
              value={spec.anchor}
              options={[
                { value: 'POINT', label: <T _str="click" swc /> },
                { value: 'LINE', label: <T _str="run" swc /> },
              ]}
              onChange={(anchor) =>
                setSpec((s) => {
                  const { handles: _handles, drawLikeWall: _drawLikeWall, ...rest } = s;
                  // Handles and wall-style drawing only exist for runs.
                  return anchor === 'LINE' ? { ...s, anchor, handles: s.handles ?? { start: [-0.5, 0], end: [0.5, 0] } } : { ...rest, anchor };
                })
              }
            />
          </FieldRow>
          <FieldRow label={<T _str="attach to" swc />} hint={spec.constraint.kind === 'WALL' ? <T _str="its back (the thick line in the drawing) rests against a wall" swc /> : undefined}>
            <Segmented
              value={spec.constraint.kind}
              options={[
                { value: 'FREE', label: <T _str="anywhere" swc /> },
                { value: 'WALL', label: <T _str="wall" swc /> },
              ]}
              onChange={(kind) => setSpec((s) => ({ ...s, constraint: kind === 'WALL' ? { kind: 'WALL', defaultInset: 0 } : { kind: 'FREE' }, ...(kind === 'WALL' ? { drawLikeWall: false } : {}) }))}
            />
          </FieldRow>
          {spec.constraint.kind === 'WALL' && (
            <FieldRow label={<T _str="gap to wall" swc />}>
              <NumberField value={spec.constraint.defaultInset ?? 0} unit="m" min={0} max={2} onCommit={(defaultInset) => setSpec((s) => ({ ...s, constraint: { kind: 'WALL', defaultInset } }))} />
            </FieldRow>
          )}
          {isLine && spec.constraint.kind === 'FREE' && (
            <ToggleRow
              label={<T _str="draw like a wall" swc />}
              hint={<T _str="click-click between any two points; the ends snap onto walls (beams)" swc />}
              checked={!!spec.drawLikeWall}
              onChange={(drawLikeWall) => setSpec((s) => ({ ...s, drawLikeWall }))}
            />
          )}
        </PanelSection>
      )}

      <PanelSection title={<T _str="default size" />}>
        {sizeAxes.map((axis) => (
          <FieldRow key={axis} label={axis === 'width' ? dimension ? <T _str="length" swc /> : <T _str="width" swc /> : axis === 'depth' ? <T _str="depth" swc /> : <T _str="height" swc />}>
            <NumberField
              value={spec.defaultSize[axis]}
              unit="m"
              min={0.01}
              max={100}
              // Live while typing, one undo step per edit.
              onEditStart={() => {
                sizeEditBase.current = store.getState().spec;
                store.begin();
              }}
              onLiveChange={(value) => resizeFromBase(axis, value)}
              onCommit={(value) => resizeFromBase(axis, value)}
              onEditEnd={() => {
                store.commit();
                sizeEditBase.current = null;
              }}
              onEditCancel={() => store.rollback()}
            />
          </FieldRow>
        ))}
        {!opening && !dimension && (
          <PanelButton onClick={() => setSpec((s) => fitFootprintToDrawing(s) ?? s)} disabled={!spec.shapes.some((s) => !s.hidden)}>
            <T _str="fit to the drawing" swc />
          </PanelButton>
        )}
      </PanelSection>

      {isLine && !opening && !dimension && (
        <PanelSection title={<T _str="resize handles" />}>
          <ToggleRow
            label={<T _str="handles at both ends" swc />}
            hint={<T _str="drag an end of a placed run to lengthen or shorten it" swc />}
            checked={!!handles?.start || !!handles?.end}
            onChange={(on) => setSpec((s) => ({ ...s, handles: on ? { ...s.handles, start: [-0.5, 0], end: [0.5, 0] } : { depth: s.handles?.depth } }))}
          />
          {(handles?.depth ?? []).map((handle, i) => (
            <div key={i} className="flex items-end gap-2">
              <FieldRow label={<T _str="depth handle" swc />}>
                <Segmented
                  value={handle.side === 1 ? 'front' : 'back'}
                  options={[
                    { value: 'back', label: <T _str="back" swc /> },
                    { value: 'front', label: <T _str="front" swc /> },
                  ]}
                  onChange={(side) => setSpec((s) => ({ ...s, handles: { ...s.handles, depth: (s.handles?.depth ?? []).map((h, j) => (j === i ? { ...h, side: side === 'front' ? 1 : -1 } : h)) } }))}
                />
              </FieldRow>
              <PanelButton danger onClick={() => setSpec((s) => ({ ...s, handles: { ...s.handles, depth: (s.handles?.depth ?? []).filter((_, j) => j !== i) } }))}>
                <TrashIcon className="h-3.5 w-3.5" />
              </PanelButton>
            </div>
          ))}
          <PanelButton onClick={() => setSpec((s) => ({ ...s, handles: { ...s.handles, depth: [...(s.handles?.depth ?? []), { u: 0, side: 1 }] } }))}>
            <PlusIcon className="h-3.5 w-3.5" />
            <T _str="add depth handle" swc />
          </PanelButton>
        </PanelSection>
      )}

      {!opening && !dimension && <ModelSettings />}

      <p className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
        <T _str="select a shape, group or parameter to edit it" swc />
      </p>
    </>
  );
};

export const ObjectSettings = memo(ObjectSettings_);
